import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Wheat, Brush, Cog, Filter, FlaskConical, Package, ArrowRight } from "lucide-react";

const STEPS = [
  { num: "01", icon: Wheat, title: "Select", desc: "Quality seeds, carefully chosen for each pressing." },
  { num: "02", icon: Brush, title: "Clean", desc: "Seeds are carefully cleaned before they go anywhere near the press." },
  { num: "03", icon: Cog, title: "Press", desc: "Traditional wooden ghani pressing — mechanical, slow, never above 45°C." },
  { num: "04", icon: Filter, title: "Settle & Filter", desc: "Naturally settled, then gently filtered. No chemical treatment at any stage." },
  { num: "05", icon: FlaskConical, title: "Test", desc: "Independently lab-tested before bottling — the reports are published on every product page." },
  { num: "06", icon: Package, title: "Bottle", desc: "Bottled in glass or food-grade plastic, sealed fresh for your kitchen." },
];

export const Process = ({ compact = false }) => (
  <section
    id="process"
    data-testid="process-section"
    className={`bg-cream py-28 md:py-36 ${compact ? "border-t border-ink/10" : ""}`}
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="mb-16 md:mb-20 md:flex md:items-end md:justify-between gap-10">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-xs tracking-[0.4em] uppercase text-amberbrand mb-4"
          >
            03 — Our Process
          </motion.p>
          <div className="overflow-hidden">
            <motion.h2
              initial={{ y: "100%" }}
              whileInView={{ y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="font-serif text-5xl md:text-7xl text-ink tracking-tighter"
            >
              From seed <span className="italic text-olive">to bottle.</span>
            </motion.h2>
          </div>
        </div>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.3 }}
          className="mt-6 md:mt-0 max-w-sm text-sm font-light text-ink/60 leading-relaxed"
        >
          Six steps, no shortcuts. This is the entire process — nothing happens
          to your oil that isn't on this page.
        </motion.p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.div
              key={s.num}
              data-testid={`process-step-${s.num}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="group bg-cream p-8 md:p-10 hover:bg-white transition-colors duration-500"
            >
              <div className="flex items-start justify-between mb-8">
                <span className="font-serif text-5xl text-olive/25 group-hover:text-olive/60 transition-colors duration-500 leading-none">
                  {s.num}
                </span>
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-olive/30 text-olive">
                  <Icon size={18} />
                </span>
              </div>
              <h3 className="font-serif text-2xl text-ink mb-3">{s.title}</h3>
              <p className="text-sm font-light text-ink/55 leading-relaxed">{s.desc}</p>
            </motion.div>
          );
        })}
      </div>

      {compact && (
        <div className="mt-12 text-center">
          <Link
            to="/process"
            data-testid="process-full-link"
            className="inline-flex items-center gap-2 text-sm tracking-[0.2em] uppercase text-amberbrand hover:text-ink transition-colors duration-300"
          >
            See the full process <ArrowRight size={16} />
          </Link>
        </div>
      )}
    </div>
  </section>
);
